/**
 * Sprint 170 — **Cómo va el negocio.** El cálculo, sin base de datos.
 *
 * José, 2026-11-01: *"lo que vale es darle estrategias de venta en base al comportamiento de su
 * negocio"*. Cuatro preguntas que la dueña se hace sola: ¿cómo voy?, ¿qué me deja más dinero?,
 * ¿qué días y a qué horas se vende?, ¿qué no se vende? — y, de ahí, qué hacer.
 *
 * ## Las reglas que lo gobiernan
 *
 * 1. **El dinero cuenta el día en que entró** (fecha del `Pago`), igual que el corte, el cierre,
 *    Reportes y el papel de la contadora. Una gráfica que dice otra cifra que la caja hace que la
 *    dueña deje de creerle a todo el sistema. Lo vigila una prueba de integración que compara cada
 *    día contra `ReporteService.ventasDelDia`.
 * 2. **"Cuándo se vende" mira la hora en que se pidió**, no la del cobro: la pregunta es de
 *    operación (cuánta carne tener lista, a qué hora reforzar), y un domicilio que se cobra al
 *    llegar se pidió una hora antes.
 * 3. **"Lo que deja" es lo que entra**, no ganancia: el catálogo no tiene costos capturados. Se dice
 *    así, en palabras, en vez de inventar un margen.
 * 4. **Sin historia no hay consejo.** Con una semana, un día de lluvia parece tendencia. Por debajo
 *    de `DIAS_MINIMOS_PARA_CONSEJO` días con venta, se dice cuántos faltan.
 * 5. **La ventana la fija el servidor**: 8 semanas completas más la que va. La pantalla no manda
 *    fechas, así que no hay nada que sanear ni forma de pedir un año entero.
 *
 * Todo aquí es puro: recibe filas y la fecha de "hoy", devuelve el tablero. Por eso se prueba sin
 * base, con días y horas exactos.
 */

/** Un pago registrado (o el total de un pedido viejo sin pagos). Montos en centavos. */
export interface CobroFila {
  pedidoId: number;
  fecha: Date;
  monto: number;
}

export interface PedidoFila {
  id: number;
  /** Cuándo se tomó el pedido. */
  creado: Date;
}

/** Un renglón vendido. `clave` es `p:<id>` para productos y `q:<id>` para paquetes. */
export interface ItemFila {
  pedidoId: number;
  clave: string;
  nombre: string;
  tipo: 'producto' | 'paquete';
  cantidad: number;
  /** Centavos. */
  subtotal: number;
  /** Productos que salieron DENTRO de este paquete (sólo cuenta para "lo que no se vende"). */
  componentes?: string[];
}

/** Lo que hoy está a la venta. */
export interface CatalogoFila {
  clave: string;
  nombre: string;
  tipo: 'producto' | 'paquete';
  creado: Date;
}

export interface EntradaTablero {
  hoy: Date;
  cobros: CobroFila[];
  pedidos: PedidoFila[];
  items: ItemFila[];
  catalogo: CatalogoFila[];
}

export interface DiaSerie {
  fecha: string;
  /** "lun", "mar"… */
  dia: string;
  monto: number;
  montoSemanaPasada: number;
  /** Todavía no llega ese día de esta semana. */
  futuro: boolean;
  esHoy: boolean;
}

export interface SemanaSerie {
  inicio: string;
  /** "27 oct" */
  etiqueta: string;
  monto: number;
  enCurso: boolean;
}

export interface ProductoRanking {
  clave: string;
  nombre: string;
  tipo: 'producto' | 'paquete';
  cantidad: number;
  ingreso: number;
  /** Porción de lo vendido en el periodo, 0–100 entero. */
  porcentaje: number;
}

export interface ProductoQuieto {
  clave: string;
  nombre: string;
  tipo: 'producto' | 'paquete';
  /** Vendido por sí solo en 4 semanas. */
  cantidad: number;
  /** Cuántos paquetes lo llevaron en 4 semanas. */
  dentroDePaquete: number;
}

export interface Consejo {
  id: string;
  titulo: string;
  detalle: string;
  accion?: { texto: string; ruta: string };
}

export interface PromedioDia {
  /** 0 = lunes … 6 = domingo. */
  indice: number;
  dia: string;
  promedio: number;
}

export interface TableroEstadisticas {
  desde: string;
  hasta: string;
  diasConVenta: number;
  diasMinimos: number;
  historiaSuficiente: boolean;
  comoVoy: {
    dias: DiaSerie[];
    llevas: number;
    semanaPasadaAlMismoDia: number;
    /** Porcentaje entero de cambio; `null` si la semana pasada fue cero. */
    cambio: number | null;
    semanas: SemanaSerie[];
    frase: string;
  };
  queDeja: {
    productos: ProductoRanking[];
    totalPeriodo: number;
    frase: string;
  };
  cuandoSeVende: {
    porDia: PromedioDia[];
    /** [día 0–6 lunes primero][hora 0–23] → promedio en centavos de ese día a esa hora. */
    mapa: number[][];
    horaDesde: number | null;
    horaHasta: number | null;
    /** [hora 0–23] → lo que entra a esa hora en una semana normal (suma de los 7 promedios). */
    porHora: number[];
    /** Los momentos (día + hora) con más venta, de mayor a menor. Máximo 5. */
    momentos: Array<{
      indice: number;
      dia: string;
      hora: number;
      franja: string;
      promedio: number;
    }>;
    frase: string;
  };
  quietos: {
    productos: ProductoQuieto[];
    frase: string;
  };
  consejos: Consejo[];
}

/** Días con venta que hacen falta para empezar a aconsejar. */
export const DIAS_MINIMOS_PARA_CONSEJO = 20;
/** Semanas completas que se miran hacia atrás. */
export const SEMANAS_ATRAS = 8;
/** Ventana de "lo que deja" y "lo que no se vende". */
export const DIAS_PRODUCTOS = 28;

const DIAS_CORTOS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
const DIAS_PLURAL = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados', 'domingos'];
const DIAS_SINGULAR = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const MESES_CORTOS = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

// ── Fechas en hora del negocio ───────────────────────────────────────────────

function inicioDelDia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Suma días de calendario (no milisegundos: así un cambio de horario no corre la fecha). */
function masDias(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** 0 = lunes … 6 = domingo. */
export function indiceLunes(d: Date): number {
  return (d.getDay() + 6) % 7;
}

function inicioDeSemana(d: Date): Date {
  return masDias(inicioDelDia(d), -indiceLunes(d));
}

export function claveDia(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Los extremos que consulta el servidor: 8 semanas completas + la que va, hasta el fin de hoy. */
export function ventanaDelTablero(hoy: Date): { inicio: Date; fin: Date } {
  const inicio = masDias(inicioDeSemana(hoy), -7 * SEMANAS_ATRAS);
  const fin = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59, 999);
  return { inicio, fin };
}

// ── Palabras ─────────────────────────────────────────────────────────────────

/** Centavos → "$12,345", sin centavos: en una frase se lee mejor redondo. */
export function dineroRedondo(centavos: number): string {
  const pesos = Math.round(centavos / 100);
  const texto = Math.abs(pesos)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${pesos < 0 ? '-' : ''}$${texto}`;
}

/** 14 → "2 de la tarde". */
export function horaEnPalabras(h: number): string {
  if (h === 0) return '12 de la noche';
  if (h === 12) return '12 del día';
  const doce = h % 12;
  if (h < 12) return `${doce} de la mañana`;
  if (h < 19) return `${doce} de la tarde`;
  return `${doce} de la noche`;
}

function franjaEnPalabras(h: number): string {
  return `de ${horaEnPalabras(h).replace(/ de la (mañana|tarde|noche)| del día/, '')} a ${horaEnPalabras((h + 1) % 24)}`;
}

function veces(n: number): string {
  return n === 1 ? '1 vez' : `${n} veces`;
}

// ── El tablero ───────────────────────────────────────────────────────────────

export function armarTablero(e: EntradaTablero): TableroEstadisticas {
  const hoy = inicioDelDia(e.hoy);
  const { inicio: ventanaInicio, fin: ventanaFin } = ventanaDelTablero(e.hoy);
  const semanaActual = inicioDeSemana(hoy);

  const cobros = e.cobros.filter((c) => c.fecha >= ventanaInicio && c.fecha <= ventanaFin);

  // Dinero por día (por la fecha del pago) y cobrado por pedido.
  const porDia = new Map<string, number>();
  const cobradoPorPedido = new Map<number, number>();
  const primeraVentaDePedido = new Map<number, Date>();
  for (const c of cobros) {
    const k = claveDia(c.fecha);
    porDia.set(k, (porDia.get(k) ?? 0) + c.monto);
    cobradoPorPedido.set(c.pedidoId, (cobradoPorPedido.get(c.pedidoId) ?? 0) + c.monto);
    const previa = primeraVentaDePedido.get(c.pedidoId);
    if (!previa || c.fecha < previa) primeraVentaDePedido.set(c.pedidoId, c.fecha);
  }
  const montoDe = (d: Date) => porDia.get(claveDia(d)) ?? 0;
  const diasConVenta = [...porDia.values()].filter((m) => m > 0).length;
  const historiaSuficiente = diasConVenta >= DIAS_MINIMOS_PARA_CONSEJO;

  // ── ¿Cómo voy? ──
  const indiceHoy = indiceLunes(hoy);
  const dias: DiaSerie[] = DIAS_CORTOS.map((dia, i) => {
    const fecha = masDias(semanaActual, i);
    const futuro = i > indiceHoy;
    return {
      fecha: claveDia(fecha),
      dia,
      monto: futuro ? 0 : montoDe(fecha),
      montoSemanaPasada: montoDe(masDias(fecha, -7)),
      futuro,
      esHoy: i === indiceHoy,
    };
  });
  const llevas = dias.filter((d) => !d.futuro).reduce((s, d) => s + d.monto, 0);
  const semanaPasadaAlMismoDia = dias
    .filter((d) => !d.futuro)
    .reduce((s, d) => s + d.montoSemanaPasada, 0);
  const cambio =
    semanaPasadaAlMismoDia > 0
      ? Math.round(((llevas - semanaPasadaAlMismoDia) / semanaPasadaAlMismoDia) * 100)
      : null;

  const semanas: SemanaSerie[] = [];
  for (let s = SEMANAS_ATRAS; s >= 0; s--) {
    const ini = masDias(semanaActual, -7 * s);
    let monto = 0;
    for (let i = 0; i < 7; i++) monto += montoDe(masDias(ini, i));
    semanas.push({
      inicio: claveDia(ini),
      etiqueta: `${ini.getDate()} ${MESES_CORTOS[ini.getMonth()]}`,
      monto,
      enCurso: s === 0,
    });
  }

  const hastaQueDia = DIAS_SINGULAR[indiceHoy];
  let fraseComoVoy: string;
  if (llevas === 0 && semanaPasadaAlMismoDia === 0) {
    fraseComoVoy = 'Esta semana todavía no hay ventas cobradas.';
  } else if (cambio === null) {
    fraseComoVoy = `Esta semana llevas ${dineroRedondo(llevas)}. La semana pasada, a estas alturas, no había ventas.`;
  } else {
    const comparacion =
      cambio === 0
        ? 'lo mismo que'
        : cambio > 0
          ? `${cambio}% más que`
          : `${Math.abs(cambio)}% menos que`;
    fraseComoVoy =
      indiceHoy === 0
        ? `Hoy lunes llevas ${dineroRedondo(llevas)}: ${comparacion} el lunes pasado (${dineroRedondo(semanaPasadaAlMismoDia)}).`
        : `Esta semana llevas ${dineroRedondo(llevas)}: ${comparacion} la semana pasada de lunes a ${hastaQueDia} (${dineroRedondo(semanaPasadaAlMismoDia)}).`;
  }
  if (llevas > 0 || semanaPasadaAlMismoDia > 0) fraseComoVoy += ' Hoy todavía no termina.';

  // ── ¿Cuándo se vende? ── (de la primera venta —o el inicio de la ventana— hasta ayer)
  const primerCobro = cobros.reduce<Date | null>(
    (min, c) => (!min || c.fecha < min ? c.fecha : min),
    null,
  );
  const inicioPromedio = primerCobro
    ? new Date(Math.max(ventanaInicio.getTime(), inicioDelDia(primerCobro).getTime()))
    : hoy;
  const ocurrencias = [0, 0, 0, 0, 0, 0, 0];
  const sumaPorDiaSemana = [0, 0, 0, 0, 0, 0, 0];
  for (let d = inicioDelDia(inicioPromedio); d < hoy; d = masDias(d, 1)) {
    ocurrencias[indiceLunes(d)] += 1;
    sumaPorDiaSemana[indiceLunes(d)] += montoDe(d);
  }
  const porDiaSemana: PromedioDia[] = DIAS_CORTOS.map((dia, i) => ({
    indice: i,
    dia,
    promedio: ocurrencias[i] > 0 ? Math.round(sumaPorDiaSemana[i] / ocurrencias[i]) : 0,
  }));

  const pedidosPorId = new Map(e.pedidos.map((p) => [p.id, p]));
  const sumaMapa = Array.from({ length: 7 }, () => new Array<number>(24).fill(0));
  for (const [pedidoId, cobrado] of cobradoPorPedido) {
    const p = pedidosPorId.get(pedidoId);
    const primera = primeraVentaDePedido.get(pedidoId);
    if (!p || !primera || primera >= hoy) continue; // hoy no ha terminado: no entra al promedio
    sumaMapa[indiceLunes(p.creado)][p.creado.getHours()] += cobrado;
  }
  const mapa = sumaMapa.map((fila, i) =>
    fila.map((v) => (ocurrencias[i] > 0 ? Math.round(v / ocurrencias[i]) : 0)),
  );
  const horasConVenta = Array.from({ length: 24 }, (_, h) => h).filter((h) =>
    mapa.some((fila) => fila[h] > 0),
  );
  const horaDesde = horasConVenta.length ? horasConVenta[0] : null;
  const horaHasta = horasConVenta.length ? horasConVenta[horasConVenta.length - 1] : null;

  const porHora = Array.from({ length: 24 }, (_, h) => mapa.reduce((s, f) => s + f[h], 0));
  const momentos = mapa
    .flatMap((fila, d) =>
      fila.map((v, h) => ({
        indice: d,
        dia: DIAS_PLURAL[d],
        hora: h,
        franja: franjaEnPalabras(h),
        promedio: v,
      })),
    )
    .filter((m) => m.promedio > 0)
    .sort((a, b) => b.promedio - a.promedio || a.indice - b.indice || a.hora - b.hora)
    .slice(0, 5);

  const abiertos = porDiaSemana.filter((d) => d.promedio > 0);
  let fraseCuando = 'Todavía no hay ventas suficientes para ver qué días y a qué horas se vende.';
  if (abiertos.length > 0) {
    const mejor = abiertos.reduce((a, b) => (b.promedio > a.promedio ? b : a));
    let celdaMax = { dia: 0, hora: 0, v: 0 };
    mapa.forEach((fila, d) =>
      fila.forEach((v, h) => {
        if (v > celdaMax.v) celdaMax = { dia: d, hora: h, v };
      }),
    );
    const totalPorHora = Array.from({ length: 24 }, (_, h) => mapa.reduce((s, f) => s + f[h], 0));
    const horaPico = totalPorHora.indexOf(Math.max(...totalPorHora));
    fraseCuando = `Tu mejor día es el ${DIAS_SINGULAR[mejor.indice]} (en promedio ${dineroRedondo(mejor.promedio)}). La hora más fuerte de la semana es ${franjaEnPalabras(horaPico)}`;
    fraseCuando +=
      celdaMax.v > 0
        ? `; el momento con más venta, los ${DIAS_PLURAL[celdaMax.dia]} ${franjaEnPalabras(celdaMax.hora)}.`
        : '.';
  }

  // ── Productos: últimas 4 semanas ──
  const inicioProductos = masDias(hoy, -(DIAS_PRODUCTOS - 1));
  const pedidosRecientes = new Set(
    [...primeraVentaDePedido.entries()].filter(([, f]) => f >= inicioProductos).map(([id]) => id),
  );
  const ranking = new Map<string, ProductoRanking>();
  const dentroDePaquete = new Map<string, number>();
  let totalPeriodo = 0;
  for (const it of e.items) {
    if (!pedidosRecientes.has(it.pedidoId)) continue;
    totalPeriodo += it.subtotal;
    const r = ranking.get(it.clave) ?? {
      clave: it.clave,
      nombre: it.nombre,
      tipo: it.tipo,
      cantidad: 0,
      ingreso: 0,
      porcentaje: 0,
    };
    r.cantidad += it.cantidad;
    r.ingreso += it.subtotal;
    ranking.set(it.clave, r);
    // Se cuenta cuántos paquetes lo llevaron, no cuántos kilos: 0.5 kg de costilla y 2 tortillas
    // no se suman entre sí, pero "salió en 6 paquetes" sí dice si alguien lo está comiendo.
    for (const clave of new Set(it.componentes ?? [])) {
      dentroDePaquete.set(clave, (dentroDePaquete.get(clave) ?? 0) + it.cantidad);
    }
  }
  const ordenados = [...ranking.values()]
    .map((r) => ({
      ...r,
      porcentaje: totalPeriodo > 0 ? Math.round((r.ingreso / totalPeriodo) * 100) : 0,
    }))
    .sort((a, b) => b.ingreso - a.ingreso || a.nombre.localeCompare(b.nombre));
  const top = ordenados.slice(0, 8);
  const estrella = top[0];
  const masVendido = [...ordenados].sort((a, b) => b.cantidad - a.cantidad)[0];

  let fraseQueDeja = 'En las últimas 4 semanas no hay ventas de productos.';
  if (estrella) {
    fraseQueDeja = `${estrella.nombre} es lo que más dinero mete: ${dineroRedondo(estrella.ingreso)} en 4 semanas, el ${estrella.porcentaje}% de lo vendido.`;
    if (masVendido && masVendido.clave !== estrella.clave) {
      fraseQueDeja += ` Lo que más veces se pide es ${masVendido.nombre} (${veces(masVendido.cantidad)}), pero deja menos.`;
    }
  }

  // ── Lo que no se vende ── (lo habilitado que ya tuvo 4 semanas para venderse)
  const cantidadesVendidas = ordenados.map((r) => r.cantidad).sort((a, b) => a - b);
  const mediana = cantidadesVendidas.length
    ? cantidadesVendidas[Math.floor(cantidadesVendidas.length / 2)]
    : 0;
  const umbral = Math.max(2, Math.round(mediana * 0.1));
  const quietos: ProductoQuieto[] = e.catalogo
    .filter((c) => c.creado < inicioProductos)
    .map((c) => ({
      clave: c.clave,
      nombre: c.nombre,
      tipo: c.tipo,
      cantidad: ranking.get(c.clave)?.cantidad ?? 0,
      dentroDePaquete: dentroDePaquete.get(c.clave) ?? 0,
    }))
    .filter((q) => q.cantidad + q.dentroDePaquete <= umbral)
    .sort(
      (a, b) =>
        a.cantidad + a.dentroDePaquete - (b.cantidad + b.dentroDePaquete) ||
        a.nombre.localeCompare(b.nombre),
    )
    .slice(0, 8);

  let fraseQuietos = 'Todo lo que tienes a la venta se movió en las últimas 4 semanas.';
  if (quietos.length > 0) {
    const nunca = quietos.filter((q) => q.cantidad + q.dentroDePaquete === 0).length;
    fraseQuietos =
      nunca > 0
        ? `${nunca === 1 ? 'Hay 1 cosa' : `Hay ${nunca} cosas`} en tu menú que no se ${nunca === 1 ? 'vendió' : 'vendieron'} ni una vez en 4 semanas.`
        : `${quietos.length === 1 ? 'Hay 1 cosa' : `Hay ${quietos.length} cosas`} que casi no se ${quietos.length === 1 ? 'vende' : 'venden'}: ${umbral} o menos en 4 semanas.`;
  }

  // ── Consejos ──
  const consejos: Consejo[] = [];
  if (!historiaSuficiente) {
    const faltan = DIAS_MINIMOS_PARA_CONSEJO - diasConVenta;
    consejos.push({
      id: 'historia',
      titulo: 'Todavía es pronto para dar consejos',
      detalle: `Hay ${diasConVenta} ${diasConVenta === 1 ? 'día' : 'días'} con ventas. Con ${faltan} más, aquí aparecerán ideas para vender más sacadas de tu propio negocio.`,
    });
  } else {
    /*
      El gancho es lo que atrae a la gente: el producto estrella, no lo que más veces se pide. En la
      primera prueba con datos reales salía el Pan de Ajo (167 veces, el más barato) como gancho de
      una cerveza: se pide mucho porque acompaña, no porque alguien venga por él.
    */
    const gancho = estrella;
    // Un paquete que no sale no se arregla metiéndolo en otro paquete: el consejo es para productos.
    const quieto = quietos.find((q) => q.tipo === 'producto');
    if (quieto && gancho && gancho.clave !== quieto.clave) {
      consejos.push({
        id: 'quieto',
        titulo:
          quieto.cantidad + quieto.dentroDePaquete === 0
            ? `Nadie pidió ${quieto.nombre} en 4 semanas`
            : `${quieto.nombre} casi no se vende`,
        detalle: `${quieto.cantidad === 0 ? 'No se vendió por separado' : `En 4 semanas se vendió ${veces(quieto.cantidad)}`}${quieto.dentroDePaquete > 0 ? ` (y salió en ${quieto.dentroDePaquete} ${quieto.dentroDePaquete === 1 ? 'paquete' : 'paquetes'})` : ''}. Ármalo en un paquete con ${gancho.nombre}, tu producto estrella, para que lo prueben. Si aun así no sale, conviene sacarlo del menú.`,
        accion: { texto: 'Armar un paquete', ruta: '/paquetes' },
      });
    }
    if (abiertos.length >= 3) {
      const media = abiertos.reduce((s, d) => s + d.promedio, 0) / abiertos.length;
      const flojo = abiertos.reduce((a, b) => (b.promedio < a.promedio ? b : a));
      const menos = Math.round((1 - flojo.promedio / media) * 100);
      if (flojo.promedio < media * 0.7) {
        consejos.push({
          id: 'dia-flojo',
          titulo: `Los ${DIAS_PLURAL[flojo.indice]} vendes ${menos}% menos que un día normal`,
          detalle: `En promedio entran ${dineroRedondo(flojo.promedio)} contra ${dineroRedondo(media)} de un día normal. Una promoción sólo para ese día puede atraer gente${gancho ? `, por ejemplo con ${gancho.nombre} de gancho` : ''}.`,
          accion: { texto: 'Crear una promoción', ruta: '/promos' },
        });
      }
    }
    if (estrella && estrella.porcentaje >= 20) {
      consejos.push({
        id: 'estrella',
        titulo: `${estrella.nombre} es tu producto estrella`,
        detalle: `Mete el ${estrella.porcentaje}% de lo que vendes. Que nunca falte, y que sea lo primero que se ofrezca al tomar el pedido.`,
      });
    }
  }

  return {
    desde: claveDia(ventanaInicio),
    hasta: claveDia(hoy),
    diasConVenta,
    diasMinimos: DIAS_MINIMOS_PARA_CONSEJO,
    historiaSuficiente,
    comoVoy: { dias, llevas, semanaPasadaAlMismoDia, cambio, semanas, frase: fraseComoVoy },
    queDeja: { productos: top, totalPeriodo, frase: fraseQueDeja },
    cuandoSeVende: {
      porDia: porDiaSemana,
      mapa,
      horaDesde,
      horaHasta,
      porHora,
      momentos,
      frase: fraseCuando,
    },
    quietos: { productos: quietos, frase: fraseQuietos },
    consejos: consejos.slice(0, 3),
  };
}
